"use client"
import { replaceRichTextLinks } from "@/utils/replaceRichTextLinks"
import styled from "@emotion/styled"
import React from "react"
import { colors, mediaQueries, sizes } from "./../../theme/theme"

// Define an interface for the style-related props.
interface RichTextWrapperStyleProps {
  /** Font size for paragraphs on mobile devices */
  mobileParagraphFontSize?: string
  /** Font weight for all paragraphs */
  paragraphFontWeight?: number
  /** Bullet size for list markers */
  markerFontSize?: string
  /** Color for list markers */
  markerColor?: string
  /** Left padding for unordered lists */
  ulPaddingLeft?: string
  /** Font weight for anchor tags */
  anchorFontWeight?: number
  /** Color for anchor tags */
  anchorColor?: string
  /** Text color for list items */
  ulListItemColor?: string
}

// Combine the richText prop with our style props.
type RichTextWrapperProps = {
  richText: string
} & RichTextWrapperStyleProps

// Create a styled container that uses our style props.
// Default values are provided via fallback expressions.
const StyledRichText = styled.div<RichTextWrapperStyleProps>`
  ${mediaQueries.mobile} {
    p {
      font-size: ${(props) => props.mobileParagraphFontSize || "19px"};
    }
  }
  p {
    font-weight: ${(props) => props.paragraphFontWeight || 700};
  }
  ul li::marker {
    font-size: ${(props) => props.markerFontSize || "1.5em"};
    color: ${(props) => props.markerColor || colors.black};
  }
  ul {
    padding-left: ${(props) => props.ulPaddingLeft || "25px"};
  }
  a {
    font-weight: ${(props) => props.anchorFontWeight || 400};
    color: ${(props) => props.anchorColor || colors.black};
  }
  ul li {
    color: ${(props) => props.ulListItemColor || colors.black};
  }
  display: flex;
  flex-direction: column;
  ul {
    display: flex;
    flex-direction: column;
    padding: 0;
    gap: 8px;
    list-style-type: none;
    li {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      color: ${colors.black};
      &::before {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        height: 100%;
        content: url("/icons/check.svg");
        margin-right: ${sizes.baseSingle};
      }
    }
  }
`

// The component passes the style props along to StyledRichText,
// making them optional so you can override defaults when needed.
const RichTextWrapper: React.FC<RichTextWrapperProps> = ({
  richText,
  mobileParagraphFontSize,
  paragraphFontWeight,
  markerFontSize,
  markerColor,
  ulPaddingLeft,
  anchorFontWeight,
  anchorColor,
  ulListItemColor,
}) => {
  return (
    <StyledRichText
      mobileParagraphFontSize={mobileParagraphFontSize}
      paragraphFontWeight={paragraphFontWeight}
      markerFontSize={markerFontSize}
      markerColor={markerColor}
      ulPaddingLeft={ulPaddingLeft}
      anchorFontWeight={anchorFontWeight}
      anchorColor={anchorColor}
      ulListItemColor={ulListItemColor}
      dangerouslySetInnerHTML={{ __html: replaceRichTextLinks(richText) }}
    />
  )
}

export default RichTextWrapper
